// app/providers.tsx
'use client'

import { usePathname, useSearchParams } from "next/navigation"
import { useEffect, Suspense, useState } from "react"
import { usePostHog } from 'posthog-js/react'

import posthog from 'posthog-js'
import { PostHogProvider as PHProvider } from 'posthog-js/react'

// Create a custom event for PostHog ready state
export const POSTHOG_READY_EVENT = 'posthog_initialized';

// Utility function to check if PostHog is properly initialized
export function isPostHogInitialized(): boolean {
  if (typeof window === 'undefined') return false;
  return Boolean(posthog && typeof posthog.identify === 'function');
}

// Debug function that can be called in dev tools
export function debugPostHog() {
  console.group('PostHog Debug Info');
  console.log('PostHog loaded:', typeof posthog !== 'undefined');
  
  if (typeof posthog !== 'undefined') {
    console.log('identify method exists:', typeof posthog.identify === 'function');
    console.log('capture method exists:', typeof posthog.capture === 'function');
    console.log('config:', posthog.config);
    console.log('persistence:', posthog.persistence);
    
    // Access these properties safely with optional chaining
    console.log('distinct_id:', posthog.get_distinct_id?.());
    console.log('isIdentified:', Boolean(posthog.get_property?.('$device_id')));
  }
  
  console.log('Is initialized according to utility:', isPostHogInitialized());
  console.groupEnd();
  
  return isPostHogInitialized() ? 'PostHog is properly initialized' : 'PostHog is NOT properly initialized';
}

// Make debug function globally available in dev environment
if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
  (window as any).__debugPostHog = debugPostHog;
  console.log('PostHog debug function available as window.__debugPostHog()');
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (isInitialized) return;

    try {
      // Check if PostHog key exists
      const apiKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
      if (!apiKey) {
        console.warn('PostHog API key not found. Analytics will not be tracked.');
        return;
      }

      // Initialize PostHog
      posthog.init(apiKey, {
        api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com',
        person_profiles: 'always',
        capture_pageview: true,
        loaded: (ph) => {
          // Mark as initialized
          console.log('PostHog initialized successfully');
          setIsInitialized(true);
          
          // Dispatch a custom event that other components can listen for
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new Event(POSTHOG_READY_EVENT));
          }
        }
      });
    } catch (error) {
      console.error('Error initializing PostHog:', error);
    }
  }, [isInitialized]);

  return (
    <PHProvider client={posthog}>
      <SuspendedPostHogPageView />
      {children}
    </PHProvider>
  )
}

function PostHogPageView() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const posthog = usePostHog()

  // Track pageviews
  useEffect(() => {
    if (pathname && posthog) {
      let url = window.origin + pathname
      if (searchParams.toString()) {
        url = url + "?" + searchParams.toString();
      }

      posthog.capture('$pageview', { '$current_url': url })
    }
  }, [pathname, searchParams, posthog])

  return null
}

// Wrap PostHogPageView in Suspense to avoid the useSearchParams usage above
// from de-opting the whole app into client-side rendering
// See: https://nextjs.org/docs/messages/deopted-into-client-rendering
function SuspendedPostHogPageView() {
  return (
    <Suspense fallback={null}>
      <PostHogPageView />
    </Suspense>
  )
}